import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStreamingChat } from './hooks/useStreamingChat';
import FeedbackModal from './components/FeedbackModal';
import ChatContainer from './components/Chat/ChatContainer';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import Sidebar from './components/Layout/Sidebar';
import { ChatInputRef } from './components/Chat/ChatInput';

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    messages,
    isLoading,
    currentStatus,
    currentTool,
    error,
    apiHealthy,
    responseComplete,
    sendMessage,
    clearError
  } = useStreamingChat();
  
  const [input, setInput] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Timer state
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Mobile sidebar state
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState<'about' | 'sources'>('about');
  
  // Sidebar sections state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    about: true,     // Keep this expanded - primary info
    sources: false   // Collapse by default - secondary info
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputRef>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Only scroll to bottom after user has started interacting
  useEffect(() => {
    if (hasUserInteracted) {
      scrollToBottom();
    }
  }, [messages, isLoading, currentStatus, currentTool, hasUserInteracted]);

  // Focus input when agent response is complete
  useEffect(() => {
    if (responseComplete && hasUserInteracted && messages.length > 1) {
      chatInputRef.current?.focus();
    }
  }, [responseComplete, hasUserInteracted, messages.length]);

  // Timer management
  useEffect(() => {
    if (isLoading) {
      // Start timer when loading begins
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      // Stop timer when loading ends
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isLoading]);

  // Format elapsed time
  const formatElapsedTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      setHasUserInteracted(true);
      const messageToSend = input.trim();
      setInput('');
      await sendMessage(messageToSend);
    }
  };

  const handleQuickAction = (question: string) => {
    setHasUserInteracted(true);
    setInput(question);
  };

  const handleClearConversation = () => {
    window.location.reload();
  };

  const getCurrentStep = () => {
    const lastAssistantMessage = [...messages].reverse().find(m => m.type === 'assistant');
    return lastAssistantMessage?.conversationStep;
  };

  const getContextualPlaceholder = () => {
    if (hasUserInteracted) {
      return t('placeholders.generic');
    }

    const currentStep = getCurrentStep();
    
    if (currentStep === 'ask_context') {
      return t('placeholders.askContext');
    } else if (currentStep === 'present_summary') {
      return t('placeholders.presentSummary');
    } else if (currentStep === 'extension_2a_ask_missing') {
      return t('placeholders.extensionMissing');
    } else {
      return t('placeholders.default');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex flex-col">
      <Header 
        apiHealthy={apiHealthy}
        getCurrentStep={getCurrentStep}
        onClearConversation={handleClearConversation}
        onShowFeedback={() => setShowFeedbackModal(true)}
        t={t}
      />

      {/* Desktop Layout: Center the entire content area */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 max-w-7xl mx-auto">
        {/* Chat area */}
        <div className="flex-1 flex flex-col min-h-0 lg:max-w-4xl lg:mr-4">
          <ChatContainer
            messages={messages}
            isLoading={isLoading}
            currentStatus={currentStatus}
            currentTool={currentTool}
            error={error}
            apiHealthy={apiHealthy}
            input={input}
            hasUserInteracted={hasUserInteracted}
            elapsedTime={elapsedTime}
            messagesEndRef={messagesEndRef}
            chatInputRef={chatInputRef}
            onSubmit={handleSubmit}
            onInputChange={setInput}
            onQuickAction={handleQuickAction}
            onClearError={clearError}
            onShowFeedback={() => setShowFeedbackModal(true)}
            onShowMobileSidebar={(section: 'about' | 'sources') => {
              setActiveMobileSection(section);
              setShowMobileSidebar(true);
            }}
            getContextualPlaceholder={getContextualPlaceholder}
            t={t}
          />
        </div>

        {/* Sidebar - positioned next to chat */}
        <div className="lg:w-80 xl:w-96 lg:flex-shrink-0 lg:mt-4">
          <Sidebar
            sectionsExpanded={sectionsExpanded}
            onToggleSection={(section) => setSectionsExpanded(prev => ({
              ...prev,
              [section]: !prev[section]
            }))}
            t={t}
          />
        </div>
      </div>

      {/* FIXED: Footer stays at bottom */}
      <Footer t={t} />
      
      {/* Mobile Sidebar Modal */}
      {showMobileSidebar && (
        <Sidebar
          isMobile={true}
          activeSection={activeMobileSection}
          onClose={() => setShowMobileSidebar(false)}
          t={t}
        />
      )}

      <FeedbackModal 
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default App;
